import { DateSelectionModal } from '../../features/process/components/date-selection.modal';
import { isValidDateRange } from '../../features/process/is-valid-date-range';
import { asPlainDate, isPlainDate, type PlainDate } from '../../core/services/plain-date';
import { openModal } from '../../core/modal/open-modal';

/**
 * Open the shared date-entry modal for one nullable calendar-day field.
 *
 * Extracted from `DateSelector` (which now calls this) so PRO-1073's Calendar-view context menu
 * edits a step's dates through the SAME modal, validation and null handling as the property
 * button does, rather than growing a second, subtly different date path. The button and the menu
 * entry are two triggers for one behaviour; only the trigger differs.
 *
 * `min` means the value must fall on or after it; `max`, on or before it. Callers pass the sibling
 * field, so the modal refuses an inverted range before it is ever sent — the API's own
 * `ConflictError` remains the backstop for a value that raced a remote edit.
 */
export const openDateSelectionModal = ({
  field,
  value,
  min,
  max,
  onUpdate,
}: {
  field: 'startDate' | 'dueDate';
  value?: PlainDate | null;
  min?: PlainDate | null;
  max?: PlainDate | null;
  onUpdate: (value: PlainDate | null) => void;
}) => {
  // `DateSelectionModal` hands back a raw `<input type="date">` value: a valid `YYYY-MM-DD`
  // string, or `null`/`''` when the native clear icon was used. This is the boundary where a real
  // `PlainDate` is minted — `asPlainDate` is only reached once `isPlainDate` has already confirmed
  // the value is a genuine calendar day, so it never throws on user input. Clearing is a real,
  // API-supported action (the field is nullable end to end), so an empty value is forwarded as
  // `null` rather than swallowed — only a non-empty, malformed value (which the native
  // `<input type="date">` should never actually produce) is dropped in place, since there is no
  // safe `PlainDate` to mint from it and nothing sensible to forward either.
  const handleSubmit = async (submitted: string | null) => {
    if (!submitted) {
      onUpdate(null);

      return;
    }

    if (isPlainDate(submitted)) {
      onUpdate(asPlainDate(submitted));
    }
  };

  const dateValidator = (candidate: string) => {
    if (min) return isValidDateRange(min, candidate);
    if (max) return isValidDateRange(candidate, max);

    return true;
  };

  return openModal(DateSelectionModal, {
    fieldName: field,
    value: value ?? undefined,
    onSubmit: handleSubmit,
    dateValidator,
  });
};
